import React, { useState } from 'react';
import { Envelope, Plus, Printer, Truck, X as XIcon } from 'phosphor-react';
import { twMerge } from 'tailwind-merge';
import {
  Buyer,
  BuyerDeliveryDocumentType,
  DeliveryAddress,
  DocumentDeliverySettings,
  InvoiceEmailConsolidation,
  getDocumentMailingAddresses,
} from '@dill/dill-shared';
import { DillButton, DillCard, DillInput, DillSelect, DillToggle } from '../../../../general/Dill';
import { COLORS } from '../../../../../utils/colors';
import { formatPhoneAsYouType, formatPhoneForDisplay, toE164 } from '../../../../../utils/phone';

type AddressFormState = Required<{ [K in keyof DeliveryAddress]: string }>;

// How one document type (invoices or statements) is delivered. Invoices and statements are set
// up independently, each with its own channels, mailing addresses and fax numbers.
export type DocumentDeliveryFormState = {
  isDisabled: boolean;
  isEmailEnabled: boolean;
  isMailEnabled: boolean;
  mailingAddresses: { key: string; address: AddressFormState }[];
  isFaxEnabled: boolean;
  faxNumbers: string[];
  // Invoices only: how a day's invoice emails are grouped.
  emailConsolidation: InvoiceEmailConsolidation;
};

export type DeliveryFormState = Record<BuyerDeliveryDocumentType, DocumentDeliveryFormState>;

const EMPTY_ADDRESS: AddressFormState = {
  name: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: '',
};

const DOCUMENT_LABELS: Record<BuyerDeliveryDocumentType, { plural: string; title: string }> = {
  INVOICE: { plural: 'invoices', title: 'Invoices' },
  STATEMENT: { plural: 'statements', title: 'Statements' },
};

const toAddressForm = (address?: DeliveryAddress | null): AddressFormState => ({
  name: address?.name || '',
  addressLine1: address?.addressLine1 || '',
  addressLine2: address?.addressLine2 || '',
  city: address?.city || '',
  state: address?.state || '',
  zipCode: address?.zipCode || '',
});

const getAddressKey = (address: AddressFormState) =>
  [address.addressLine1, address.addressLine2, address.city, address.state, address.zipCode]
    .map((part) => part.trim().toLowerCase())
    .join('|');

let addressKeyCounter = 0;
const newAddressKey = () => `address-${++addressKeyCounter}`;

const EMAIL_CONSOLIDATION_OPTIONS: { value: InvoiceEmailConsolidation; label: string }[] = [
  { value: 'SEPARATE', label: 'Each invoice in its own email' },
  { value: 'DAILY_SEPARATE_PDFS', label: "One email per day, each invoice's PDF attached" },
  { value: 'DAILY_COMBINED_PDF', label: 'One email per day, all invoices in one PDF' },
];

const getDocumentTypes = (isInvoicingEnabled: boolean): BuyerDeliveryDocumentType[] =>
  isInvoicingEnabled ? ['INVOICE', 'STATEMENT'] : ['STATEMENT'];

/**
 * getInitialDeliveryState - Loads the buyer's saved invoice and statement settings into the form.
 * Saved addresses and fax numbers are shown even when their channel is off, so switching it back
 * on doesn't lose them.
 */
export const getInitialDeliveryState = (buyer: Buyer | null | undefined): DeliveryFormState => {
  const toDocumentForm = (documentType: BuyerDeliveryDocumentType): DocumentDeliveryFormState => {
    const settings =
      documentType === 'INVOICE'
        ? buyer?.invoiceDeliverySettings
        : buyer?.statementDeliverySettings;
    // Before these settings existed, invoice emailing was switched off via isInvoiceNoticesDisabled.
    const legacyEmailEnabled = documentType === 'INVOICE' ? !buyer?.isInvoiceNoticesDisabled : true;
    return {
      isDisabled: !!settings?.isDisabled,
      isEmailEnabled: settings?.isEmailEnabled ?? legacyEmailEnabled,
      isMailEnabled: !!settings?.isMailEnabled,
      mailingAddresses: getDocumentMailingAddresses(settings).map((address) => ({
        key: newAddressKey(),
        address: toAddressForm(address),
      })),
      isFaxEnabled: !!settings?.isFaxEnabled,
      faxNumbers: [...new Set(settings?.faxNumbers || [])],
      emailConsolidation: settings?.emailConsolidation ?? 'SEPARATE',
    };
  };
  return { INVOICE: toDocumentForm('INVOICE'), STATEMENT: toDocumentForm('STATEMENT') };
};

/**
 * toDocumentDeliverySettings - The settings stored for one document type.
 */
export const toDocumentDeliverySettings = (
  state: DeliveryFormState,
  documentType: BuyerDeliveryDocumentType
): DocumentDeliverySettings => {
  const documentState = state[documentType];
  const mailingAddresses = documentState.mailingAddresses.map(({ address }) => ({
    ...address,
    state: address.state.trim().toUpperCase(),
  }));
  return {
    isDisabled: documentState.isDisabled,
    isEmailEnabled: documentState.isEmailEnabled,
    isMailEnabled: documentState.isMailEnabled && mailingAddresses.length > 0,
    mailingAddresses,
    isFaxEnabled: documentState.isFaxEnabled && documentState.faxNumbers.length > 0,
    faxNumbers: documentState.faxNumbers,
    ...(documentType === 'INVOICE' ? { emailConsolidation: documentState.emailConsolidation } : {}),
  };
};

const validateDocumentDeliveryState = (state: DocumentDeliveryFormState): string | null => {
  if (state.isDisabled) return null;
  if (state.isMailEnabled) {
    if (state.mailingAddresses.length === 0) {
      return 'add a mailing address, or turn mail off';
    }
    for (const { address } of state.mailingAddresses) {
      if (
        !address.addressLine1.trim() ||
        !address.city.trim() ||
        !address.state.trim() ||
        !address.zipCode.trim()
      ) {
        return 'each mailing address needs a street, city, state and ZIP code';
      }
      if (!/^[A-Za-z]{2}$/.test(address.state.trim())) {
        return 'mailing address state must be a 2-letter code';
      }
      if (!/^\d{5}(-\d{4})?$/.test(address.zipCode.trim())) {
        return 'mailing address ZIP code must be 5 digits (or ZIP+4)';
      }
    }
    const addressKeys = state.mailingAddresses.map(({ address }) => getAddressKey(address));
    if (new Set(addressKeys).size !== addressKeys.length) {
      return 'the same mailing address is added twice';
    }
  }
  if (state.isFaxEnabled) {
    if (state.faxNumbers.length === 0) {
      return 'add a fax number, or turn fax off';
    }
    // Catches numbers saved before fax numbers were checked against real numbering plans.
    const invalid = state.faxNumbers.find((faxNumber) => !toE164(faxNumber));
    if (invalid) {
      return `${invalid} isn't a valid fax number - remove it to save`;
    }
  }
  return null;
};

export const validateDeliveryState = (
  state: DeliveryFormState,
  isInvoicingEnabled: boolean
): string | null => {
  for (const documentType of getDocumentTypes(isInvoicingEnabled)) {
    const error = validateDocumentDeliveryState(state[documentType]);
    if (error) return `${DOCUMENT_LABELS[documentType].title}: ${error}`;
  }
  return null;
};

function ChannelRow({
  icon,
  title,
  description,
  value,
  onChange,
  disabled,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-start gap-2 min-w-0">
          <div className="mt-0.5 shrink-0">{icon}</div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900">{title}</p>
            <p className="text-xs text-gray-500 mt-0.5">{description}</p>
          </div>
        </div>
        <DillToggle value={value} onChange={(v) => !disabled && onChange(v)} />
      </div>
      {value && children && <div className="mt-3">{children}</div>}
    </div>
  );
}

function DocumentDeliverySection({
  buyer,
  documentType,
  value: current,
  onChange,
  copySource,
  disabled,
  isMailAndFaxAvailable,
}: {
  buyer?: Buyer | null;
  documentType: BuyerDeliveryDocumentType;
  value: DocumentDeliveryFormState;
  onChange: (next: DocumentDeliveryFormState) => void;
  // The other document type's settings, offered as a one-click copy.
  copySource?: { documentType: BuyerDeliveryDocumentType; value: DocumentDeliveryFormState };
  disabled?: boolean;
  isMailAndFaxAvailable: boolean;
}) {
  const [faxInput, setFaxInput] = useState('');
  const [faxInputError, setFaxInputError] = useState('');
  const labels = DOCUMENT_LABELS[documentType];

  const update = (next: Partial<DocumentDeliveryFormState>) => onChange({ ...current, ...next });

  const updateAddressField = (key: string, field: keyof AddressFormState, fieldValue: string) =>
    update({
      mailingAddresses: current.mailingAddresses.map((entry) =>
        entry.key === key ? { ...entry, address: { ...entry.address, [field]: fieldValue } } : entry
      ),
    });

  const getBillingAddress = (): AddressFormState => ({
    ...EMPTY_ADDRESS,
    name: buyer?.name || '',
    addressLine1: buyer?.address || '',
    city: buyer?.city || '',
    state: buyer?.state || '',
    zipCode: buyer?.zipCode || '',
  });

  const handleMailToggle = (isMailEnabled: boolean) =>
    update({
      isMailEnabled,
      // Start with one blank address the first time mail is switched on.
      mailingAddresses:
        isMailEnabled && current.mailingAddresses.length === 0
          ? [{ key: newAddressKey(), address: EMPTY_ADDRESS }]
          : current.mailingAddresses,
    });

  const handleAddAddress = () =>
    update({
      mailingAddresses: [
        ...current.mailingAddresses,
        { key: newAddressKey(), address: EMPTY_ADDRESS },
      ],
    });

  const handleAddFaxNumber = () => {
    // Numbers without a country code are read as US; anything else needs a leading +.
    const normalized = toE164(faxInput);
    if (!normalized) {
      setFaxInputError(
        'Enter a valid fax number, e.g. (212) 555-1234, or +44 20 7183 8750 outside the US'
      );
      return;
    }
    if (current.faxNumbers.includes(normalized)) {
      setFaxInputError('Fax number already added');
      return;
    }
    update({ faxNumbers: [...current.faxNumbers, normalized] });
    setFaxInput('');
    setFaxInputError('');
  };

  const handleCopy = () => {
    if (!copySource) return;
    const source = copySource.value;
    onChange({
      ...source,
      mailingAddresses: source.mailingAddresses.map((entry) => ({
        ...entry,
        key: newAddressKey(),
      })),
      faxNumbers: [...source.faxNumbers],
      // Grouping is invoice-only, so copying channels never changes it.
      emailConsolidation: current.emailConsolidation,
    });
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
      <div className="flex items-center justify-between gap-3 px-1">
        <p className="text-sm font-semibold text-gray-900">{labels.title}</p>
        <div className="flex items-center gap-3">
          {copySource && !current.isDisabled && (
            <button
              type="button"
              onClick={handleCopy}
              disabled={disabled}
              className="text-xs font-medium text-blue-600 hover:text-blue-700 bg-transparent border-none cursor-pointer">
              Copy from {DOCUMENT_LABELS[copySource.documentType].plural}
            </button>
          )}
          <span className="text-xs text-gray-500">Send {labels.plural}</span>
          <DillToggle
            value={!current.isDisabled}
            onChange={(v) => !disabled && update({ isDisabled: !v })}
          />
        </div>
      </div>

      {current.isDisabled ? (
        <p className="mt-2 px-1 text-xs text-gray-500">
          Automated {labels.plural} are turned off for this customer, including for users added
          later.
        </p>
      ) : (
        <div className="mt-3 flex flex-col gap-2">
          <ChannelRow
            icon={<Envelope size={16} color={COLORS.PRIMARY_500} />}
            title="Email"
            description="Email to the users enabled under User Email Preferences."
            value={current.isEmailEnabled}
            onChange={(isEmailEnabled) => update({ isEmailEnabled })}
            disabled={disabled}
          />
          {documentType === 'INVOICE' && current.isEmailEnabled && (
            <div className="ml-8 flex flex-col gap-1">
              <p className="text-xs font-medium text-gray-700">Group invoice emails</p>
              <div className="max-w-[360px]">
                <DillSelect
                  value={current.emailConsolidation}
                  onChange={(emailConsolidation) =>
                    update({ emailConsolidation: emailConsolidation as InvoiceEmailConsolidation })
                  }
                  options={EMAIL_CONSOLIDATION_OPTIONS}
                  disabled={disabled}
                />
              </div>
              <p className="text-[11px] text-gray-500">
                Applies to emails only. Mailed and faxed invoices are always sent one at a time.
              </p>
            </div>
          )}

          {isMailAndFaxAvailable && (
            <>
              <ChannelRow
                icon={<Envelope size={16} color={COLORS.PRIMARY_500} weight="fill" />}
                title="Mail"
                description={`Print and mail ${labels.plural} to every address below.`}
                value={current.isMailEnabled}
                onChange={handleMailToggle}
                disabled={disabled}>
                <div className="flex flex-col gap-3">
                  {current.mailingAddresses.map((entry, index) => (
                    <div
                      key={entry.key}
                      className={twMerge(
                        'flex flex-col gap-2',
                        index > 0 && 'border-t border-gray-100 pt-3'
                      )}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium text-gray-500">
                          Address {index + 1}
                        </span>
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() =>
                              update({
                                mailingAddresses: current.mailingAddresses.map((item) =>
                                  item.key === entry.key
                                    ? { ...item, address: getBillingAddress() }
                                    : item
                                ),
                              })
                            }
                            disabled={disabled}
                            className="text-xs font-medium text-blue-600 hover:text-blue-700 bg-transparent border-none cursor-pointer">
                            Copy from billing address
                          </button>
                          {current.mailingAddresses.length > 1 && (
                            <button
                              type="button"
                              onClick={() =>
                                update({
                                  mailingAddresses: current.mailingAddresses.filter(
                                    (item) => item.key !== entry.key
                                  ),
                                })
                              }
                              disabled={disabled}
                              className="text-xs font-medium text-gray-500 hover:text-red-600 bg-transparent border-none cursor-pointer">
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                      <DillInput
                        value={entry.address.name}
                        onChange={(v) => updateAddressField(entry.key, 'name', v)}
                        placeholder="Recipient name"
                        disabled={disabled}
                      />
                      <DillInput
                        value={entry.address.addressLine1}
                        onChange={(v) => updateAddressField(entry.key, 'addressLine1', v)}
                        placeholder="Street address"
                        disabled={disabled}
                      />
                      <DillInput
                        value={entry.address.addressLine2}
                        onChange={(v) => updateAddressField(entry.key, 'addressLine2', v)}
                        placeholder="Suite, unit, attn. (optional)"
                        disabled={disabled}
                      />
                      <div className="grid grid-cols-[1fr_80px_110px] gap-2">
                        <DillInput
                          value={entry.address.city}
                          onChange={(v) => updateAddressField(entry.key, 'city', v)}
                          placeholder="City"
                          disabled={disabled}
                        />
                        <DillInput
                          value={entry.address.state}
                          onChange={(v) => updateAddressField(entry.key, 'state', v)}
                          placeholder="State"
                          disabled={disabled}
                        />
                        <DillInput
                          value={entry.address.zipCode}
                          onChange={(v) => updateAddressField(entry.key, 'zipCode', v)}
                          placeholder="ZIP"
                          disabled={disabled}
                        />
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={handleAddAddress}
                    disabled={disabled}
                    className="inline-flex items-center gap-1 self-start text-xs font-medium text-blue-600 hover:text-blue-700 bg-transparent border-none cursor-pointer">
                    <Plus size={12} weight="bold" />
                    Add another address
                  </button>
                </div>
              </ChannelRow>

              <ChannelRow
                icon={<Printer size={16} color={COLORS.PRIMARY_500} />}
                title="Fax"
                description={`Fax ${labels.plural} to every number below.`}
                value={current.isFaxEnabled}
                onChange={(isFaxEnabled) => update({ isFaxEnabled })}
                disabled={disabled}>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <DillInput
                      value={faxInput}
                      onChange={(v) => {
                        setFaxInput(formatPhoneAsYouType(v));
                        setFaxInputError('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddFaxNumber();
                        }
                      }}
                      placeholder="Fax number"
                      disabled={disabled}
                      error={faxInputError}
                    />
                  </div>
                  <DillButton
                    label="Add"
                    variant="secondary"
                    onClick={handleAddFaxNumber}
                    isDisabled={!faxInput || disabled}
                    height={36}
                  />
                </div>
                {faxInputError && <p className="mt-1 text-xs text-red-600">{faxInputError}</p>}
                {current.faxNumbers.length > 0 && (
                  <div className="mt-3 flex flex-col gap-2">
                    {current.faxNumbers.map((faxNumber) => (
                      <div
                        key={faxNumber}
                        className="flex items-center justify-between gap-3 rounded-md border border-gray-200 px-3 py-2">
                        <span className="text-sm font-medium text-gray-900">
                          {formatPhoneForDisplay(faxNumber)}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            update({
                              faxNumbers: current.faxNumbers.filter((item) => item !== faxNumber),
                            })
                          }
                          disabled={disabled}
                          className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600 bg-transparent border-none cursor-pointer">
                          <XIcon size={12} weight="bold" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </ChannelRow>
            </>
          )}

          {!current.isEmailEnabled &&
            !(isMailAndFaxAvailable && (current.isMailEnabled || current.isFaxEnabled)) && (
              <p className="text-xs text-amber-700">
                No delivery channel is on, so this customer won&apos;t receive {labels.plural}.
              </p>
            )}
        </div>
      )}
    </div>
  );
}

function DocumentDeliverySettingsCard({
  buyer,
  value,
  onChange,
  disabled,
  isInvoicingEnabled,
  isMailAndFaxAvailable,
  error,
}: {
  buyer?: Buyer | null;
  value: DeliveryFormState;
  onChange: (next: DeliveryFormState) => void;
  disabled?: boolean;
  isInvoicingEnabled: boolean;
  isMailAndFaxAvailable: boolean;
  error?: string | null;
}) {
  const documentTypes = getDocumentTypes(isInvoicingEnabled);

  return (
    <DillCard className="bg-white">
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-purple-50">
          <Truck size={16} color={COLORS.PRIMARY_500} weight="bold" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-gray-900">
            {isInvoicingEnabled ? 'Invoice & Statement Delivery' : 'Statement Delivery'}
          </h4>
          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
            {isInvoicingEnabled
              ? 'Set up how this customer receives invoices and statements. Channels can be combined.'
              : 'Choose how this customer receives statements. Channels can be combined.'}
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-3">
        {documentTypes.map((documentType, index) => {
          const previousType = index > 0 ? documentTypes[index - 1] : null;
          return (
            <DocumentDeliverySection
              key={documentType}
              buyer={buyer}
              documentType={documentType}
              value={value[documentType]}
              onChange={(next) => onChange({ ...value, [documentType]: next })}
              copySource={
                previousType
                  ? { documentType: previousType, value: value[previousType] }
                  : undefined
              }
              disabled={disabled}
              isMailAndFaxAvailable={isMailAndFaxAvailable}
            />
          );
        })}
      </div>

      {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
    </DillCard>
  );
}

export default DocumentDeliverySettingsCard;
